import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Card,
    CardContent,
    CardMedia,
    Typography,
    Grid,
    Chip,
    Button,
    CircularProgress,
    Alert,
    LinearProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Tabs,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Tooltip,
    IconButton,
    Switch,
    FormControlLabel,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
} from '@mui/material';
import {
    School as SchoolIcon,
    PlayArrow as PlayIcon,
    CheckCircle as CheckCircleIcon,
    Schedule as ScheduleIcon,
    Business as BusinessIcon,
    Group as GroupIcon,
    Refresh as RefreshIcon,
    Info as InfoIcon,
    Warning as WarningIcon,
    Settings as SettingsIcon,
} from '@mui/icons-material';
import { useTranslate, useGetList, Title } from 'react-admin';

// Helper to get auth token
const getAuthToken = () => localStorage.getItem('token');

// Ensure API_URL ends with /api
const baseApiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const API_URL = baseApiUrl.endsWith('/api') ? baseApiUrl : `${baseApiUrl}/api`;

interface License {
    id: number;
    uuid: string;
    type: 'reference' | 'copy';
    status: string;
    expiresAt: string | null;
    canEnroll: boolean;
    enrollmentStats: {
        current: number;
        max: number | null;
        unlimited: boolean;
        available: number | null;
    };
    licensorCompanyId: number;
    autoSyncUpdates?: boolean;
    lastSyncedAt?: string | null;
    isActiveForLicensee: boolean;
    enrollmentModeForLicensee: string;
}

interface LicensedCourse {
    id: number;
    title: string;
    description: string;
    thumbnailUrl: string | null;
    difficulty: string;
    estimatedDuration: number;
    tags: string[];
    lessonCount: number;
    catalog: {
        id: number;
        name: string;
    } | null;
    license: License;
    isEnrolled: boolean;
    isLicensed: boolean;
    isOwnCourse: boolean;
}

interface Enrollment {
    id: number;
    userId: number;
    status: string;
    progress: number;
    enrolledAt: string;
    startedAt: string | null;
    completedAt: string | null;
}

interface LicenseEnrollmentsData {
    enrollments: Enrollment[];
    total: number;
    license: {
        id: number;
        currentEnrollments: number;
        maxEnrollments: number | null;
    };
}

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

function TabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;
    return (
        <div role="tabpanel" hidden={value !== index} {...other}>
            {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
        </div>
    );
}

const LicensedCoursesPage: React.FC = () => {
    const translate = useTranslate();

    const { data: users = [] } = useGetList('users', {
        pagination: { page: 1, perPage: 500 },
        sort: { field: 'lastName', order: 'ASC' },
    });
    const formatUser = useCallback((userId: number) => {
        const u = users.find((x: any) => Number(x.id) === Number(userId));
        if (!u) return `#${userId}`;
        const name = [u.firstName, u.lastName].filter(Boolean).join(' ');
        return name || u.email || `#${userId}`;
    }, [users]);

    const [courses, setCourses] = useState<LicensedCourse[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [tabValue, setTabValue] = useState(0);
    const [enrollingCourseId, setEnrollingCourseId] = useState<number | null>(null);
    const [selectedCourse, setSelectedCourse] = useState<LicensedCourse | null>(null);
    const [detailDialogOpen, setDetailDialogOpen] = useState(false);
    const [enrollmentsDialogOpen, setEnrollmentsDialogOpen] = useState(false);
    const [enrollmentsData, setEnrollmentsData] = useState<LicenseEnrollmentsData | null>(null);
    const [loadingEnrollments, setLoadingEnrollments] = useState(false);
    const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);
    const [settingsCourse, setSettingsCourse] = useState<LicensedCourse | null>(null);
    const [savingSettings, setSavingSettings] = useState(false);

    const fetchLicensedCourses = useCallback(async () => {
        setLoading(true);
        setError(null);

        try {
            const token = getAuthToken();
            const response = await fetch(`${API_URL}/licensed-courses`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Accept': 'application/json',
                },
            });

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
            }

            const data = await response.json();
            setCourses(data.courses || []);
        } catch (err) {
            console.error('Error fetching licensed courses:', err);
            setError('Fehler beim Laden der lizenzierten Kurse');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchLicensedCourses();
    }, [fetchLicensedCourses]);

    const handleEnroll = async (course: LicensedCourse) => {
        setEnrollingCourseId(course.id);

        try {
            const token = getAuthToken();
            const response = await fetch(`${API_URL}/licensed-courses/${course.id}/enroll`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                },
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.detail || errorData.message || 'Einschreibung fehlgeschlagen');
            }

            // Refresh the list
            await fetchLicensedCourses();
        } catch (err: any) {
            setError(err.message || 'Fehler bei der Einschreibung');
        } finally {
            setEnrollingCourseId(null);
        }
    };

    const handleViewDetail = (course: LicensedCourse) => {
        setSelectedCourse(course);
        setDetailDialogOpen(true);
    };

    const handleViewEnrollments = async (course: LicensedCourse) => {
        setSelectedCourse(course);
        setEnrollmentsDialogOpen(true);
        setLoadingEnrollments(true);

        try {
            const token = getAuthToken();
            const response = await fetch(
                `${API_URL}/licensed-courses/license/${course.license.id}/enrollments`,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Accept': 'application/json',
                    },
                }
            );

            if (response.ok) {
                const data = await response.json();
                setEnrollmentsData(data);
            }
        } catch (err) {
            console.error('Error fetching enrollments:', err);
        } finally {
            setLoadingEnrollments(false);
        }
    };

    const handleOpenSettings = (course: LicensedCourse) => {
        setSettingsCourse(course);
        setSettingsDialogOpen(true);
    };

    const handleUpdateSettings = async (field: string, value: any) => {
        if (!settingsCourse) return;
        setSavingSettings(true);

        try {
            const token = getAuthToken();
            const response = await fetch(
                `${API_URL}/licensed-courses/${settingsCourse.license.id}/settings`,
                {
                    method: 'PATCH',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Accept': 'application/json',
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({ [field]: value }),
                }
            );

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
            }

            // Update local state
            setCourses(prev => prev.map(c => {
                if (c.id === settingsCourse.id) {
                    return {
                        ...c,
                        license: {
                            ...c.license,
                            [field]: value,
                        },
                    };
                }
                return c;
            }));

            // Update settings dialog state
            setSettingsCourse(prev => prev ? {
                ...prev,
                license: {
                    ...prev.license,
                    [field]: value,
                },
            } : null);
        } catch (err) {
            console.error('Error updating settings:', err);
            setError('Fehler beim Speichern der Einstellungen');
        } finally {
            setSavingSettings(false);
        }
    };

    const getDifficultyColor = (difficulty: string) => {
        switch (difficulty) {
            case 'beginner':
                return 'success';
            case 'intermediate':
                return 'warning';
            case 'advanced':
                return 'error';
            default:
                return 'default';
        }
    };

    const getDifficultyLabel = (difficulty: string) => {
        switch (difficulty) {
            case 'beginner':
                return 'Einstieg';
            case 'intermediate':
                return 'Vertiefung';
            case 'advanced':
                return 'Spezialisierung';
            default:
                return difficulty;
        }
    };

    const formatDuration = (minutes: number) => {
        if (minutes < 60) return `${minutes} Min.`;
        const hours = Math.floor(minutes / 60);
        const mins = minutes % 60;
        return mins > 0 ? `${hours}h ${mins}min` : `${hours}h`;
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'active':
                return 'success';
            case 'pending':
                return 'warning';
            case 'expired':
            case 'revoked':
                return 'error';
            default:
                return 'default';
        }
    };

    const getStatusLabel = (status: string) => {
        const labels: Record<string, string> = {
            active: 'Aktiv',
            pending: 'Ausstehend',
            expired: 'Abgelaufen',
            revoked: 'Widerrufen',
        };
        return labels[status] ?? status;
    };

    const getEnrollmentStatusLabel = (status: string) => {
        const labels: Record<string, string> = {
            enrolled: 'Eingeschrieben',
            in_progress: 'In Bearbeitung',
            completed: 'Abgeschlossen',
        };
        return labels[status] ?? status;
    };

    const referenceCourses = courses.filter(c => c.license.type === 'reference');
    const copyCourses = courses.filter(c => c.license.type === 'copy');

    const renderCourseCard = (course: LicensedCourse) => (
        <Grid size={{ xs: 12, sm: 6, md: 4 }} key={course.id}>
            <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                {course.thumbnailUrl ? (
                    <CardMedia
                        component="img"
                        height="160"
                        image={course.thumbnailUrl}
                        alt={course.title}
                        sx={{ objectFit: 'cover' }}
                    />
                ) : (
                    <Box sx={{ height: 160, bgcolor: 'grey.200', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <SchoolIcon sx={{ fontSize: 48, color: 'grey.400' }} />
                    </Box>
                )}
                <CardContent sx={{ flexGrow: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                            <Chip
                                label={course.license.type === 'reference' ? 'Referenz' : 'Kopie'}
                                size="small"
                                color={course.license.type === 'reference' ? 'primary' : 'secondary'}
                            />
                            <Chip
                                label={course.license.isActiveForLicensee ? 'Aktiv' : 'Inaktiv'}
                                size="small"
                                color={course.license.isActiveForLicensee ? 'success' : 'default'}
                            />
                        </Box>
                        <Chip
                            label={getStatusLabel(course.license.status)}
                            size="small"
                            color={getStatusColor(course.license.status) as any}
                            variant="outlined"
                        />
                    </Box>

                    <Typography variant="h6" gutterBottom noWrap>
                        {course.title}
                    </Typography>

                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            mb: 2,
                        }}
                    >
                        {course.description}
                    </Typography>

                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                        <Chip
                            icon={<SchoolIcon />}
                            label={getDifficultyLabel(course.difficulty)}
                            size="small"
                            color={getDifficultyColor(course.difficulty) as any}
                            variant="outlined"
                        />
                        <Chip
                            icon={<ScheduleIcon />}
                            label={formatDuration(course.estimatedDuration)}
                            size="small"
                            variant="outlined"
                        />
                        <Chip
                            label={`${course.lessonCount} Lektionen`}
                            size="small"
                            variant="outlined"
                        />
                    </Box>

                    {/* Enrollment Stats */}
                    <Box sx={{ mb: 2 }}>
                        <Typography variant="caption" color="text.secondary">
                            Einschreibungen: {course.license.enrollmentStats.current}
                            {course.license.enrollmentStats.max !== null
                                ? ` / ${course.license.enrollmentStats.max}`
                                : ' (unbegrenzt)'}
                        </Typography>
                        {course.license.enrollmentStats.max !== null && (
                            <LinearProgress
                                variant="determinate"
                                value={(course.license.enrollmentStats.current / course.license.enrollmentStats.max) * 100}
                                sx={{ mt: 0.5 }}
                            />
                        )}
                    </Box>

                    {course.license.expiresAt && (
                        <Typography variant="caption" color="text.secondary" display="block">
                            Läuft ab: {new Date(course.license.expiresAt).toLocaleDateString('de-DE')}
                        </Typography>
                    )}
                </CardContent>

                <Box sx={{ p: 2, pt: 0 }}>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        {course.isEnrolled ? (
                            <Button
                                variant="contained"
                                color="success"
                                startIcon={<PlayIcon />}
                                fullWidth
                                href={`/#/courses/${course.id}/play`}
                            >
                                Fortsetzen
                            </Button>
                        ) : course.license.canEnroll ? (
                            <Button
                                variant="contained"
                                color="primary"
                                startIcon={enrollingCourseId === course.id ? <CircularProgress size={20} /> : <SchoolIcon />}
                                onClick={() => handleEnroll(course)}
                                disabled={enrollingCourseId === course.id}
                                fullWidth
                            >
                                Einschreiben
                            </Button>
                        ) : (
                            <Button variant="outlined" disabled fullWidth>
                                Nicht verfügbar
                            </Button>
                        )}
                        <Tooltip title="Kurseinstellungen">
                            <IconButton onClick={() => handleOpenSettings(course)}>
                                <SettingsIcon />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Details">
                            <IconButton onClick={() => handleViewDetail(course)}>
                                <InfoIcon />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Einschreibungen">
                            <IconButton onClick={() => handleViewEnrollments(course)}>
                                <GroupIcon />
                            </IconButton>
                        </Tooltip>
                    </Box>
                </Box>
            </Card>
        </Grid>
    );

    if (loading) {
        return (
            <Box sx={{ p: 3 }}>
                <Title title="Lizenzierte Kurse" />
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress />
                </Box>
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3 }}>
            <Title title="Lizenzierte Kurse" />

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h4">Lizenzierte Kurse</Typography>
                <Button
                    startIcon={<RefreshIcon />}
                    onClick={fetchLicensedCourses}
                    variant="outlined"
                >
                    Aktualisieren
                </Button>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            {courses.length === 0 ? (
                <Alert severity="info" icon={<BusinessIcon />}>
                    Keine lizenzierten Kurse vorhanden. Besuchen Sie den Marktplatz, um Kurse von anderen Unternehmen zu lizenzieren.
                </Alert>
            ) : (
                <>
                    <Paper sx={{ mb: 3 }}>
                        <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)}>
                            <Tab label={`Alle (${courses.length})`} />
                            <Tab label={`Referenz-Lizenzen (${referenceCourses.length})`} />
                            <Tab label={`Kopie-Lizenzen (${copyCourses.length})`} />
                        </Tabs>
                    </Paper>

                    <TabPanel value={tabValue} index={0}>
                        <Grid container spacing={3}>
                            {courses.map(renderCourseCard)}
                        </Grid>
                    </TabPanel>

                    <TabPanel value={tabValue} index={1}>
                        {referenceCourses.length === 0 ? (
                            <Alert severity="info">Keine Referenz-Lizenzen vorhanden.</Alert>
                        ) : (
                            <Grid container spacing={3}>
                                {referenceCourses.map(renderCourseCard)}
                            </Grid>
                        )}
                    </TabPanel>

                    <TabPanel value={tabValue} index={2}>
                        {copyCourses.length === 0 ? (
                            <Alert severity="info">Keine Kopie-Lizenzen vorhanden.</Alert>
                        ) : (
                            <Grid container spacing={3}>
                                {copyCourses.map(renderCourseCard)}
                            </Grid>
                        )}
                    </TabPanel>
                </>
            )}

            {/* Course Detail Dialog */}
            <Dialog open={detailDialogOpen} onClose={() => setDetailDialogOpen(false)} maxWidth="md" fullWidth>
                {selectedCourse && (
                    <>
                        <DialogTitle>{selectedCourse.title}</DialogTitle>
                        <DialogContent>
                            <Grid container spacing={2}>
                                <Grid size={{ xs: 12, md: 6 }}>
                                    <Typography variant="subtitle2" gutterBottom>Kursinformationen</Typography>
                                    <Typography variant="body2"><strong>Schwierigkeit:</strong> {getDifficultyLabel(selectedCourse.difficulty)}</Typography>
                                    <Typography variant="body2"><strong>Dauer:</strong> {formatDuration(selectedCourse.estimatedDuration)}</Typography>
                                    <Typography variant="body2"><strong>Lektionen:</strong> {selectedCourse.lessonCount}</Typography>
                                    <Typography variant="body2"><strong>Katalog:</strong> {selectedCourse.catalog?.name || 'Kein Katalog'}</Typography>
                                    {selectedCourse.tags?.length > 0 && (
                                        <Box sx={{ mt: 1 }}>
                                            {selectedCourse.tags.map((tag, i) => (
                                                <Chip key={i} label={tag} size="small" sx={{ mr: 0.5, mb: 0.5 }} />
                                            ))}
                                        </Box>
                                    )}
                                </Grid>
                                <Grid size={{ xs: 12, md: 6 }}>
                                    <Typography variant="subtitle2" gutterBottom>Lizenzinformationen</Typography>
                                    <Typography variant="body2">
                                        <strong>Typ:</strong> {selectedCourse.license.type === 'reference' ? 'Referenz-Lizenz' : 'Kopie-Lizenz'}
                                    </Typography>
                                    <Typography variant="body2"><strong>Status:</strong> {getStatusLabel(selectedCourse.license.status)}</Typography>
                                    <Typography variant="body2">
                                        <strong>Einschreibungen:</strong> {selectedCourse.license.enrollmentStats.current}
                                        {selectedCourse.license.enrollmentStats.max !== null
                                            ? ` / ${selectedCourse.license.enrollmentStats.max}`
                                            : ' (unbegrenzt)'}
                                    </Typography>
                                    {selectedCourse.license.expiresAt && (
                                        <Typography variant="body2">
                                            <strong>Läuft ab:</strong> {new Date(selectedCourse.license.expiresAt).toLocaleDateString('de-DE')}
                                        </Typography>
                                    )}
                                    {selectedCourse.license.type === 'reference' && (
                                        <Typography variant="body2">
                                            <strong>Auto-Sync:</strong> {selectedCourse.license.autoSyncUpdates ? 'Aktiviert' : 'Deaktiviert'}
                                        </Typography>
                                    )}
                                </Grid>
                                <Grid size={{ xs: 12 }}>
                                    <Typography variant="subtitle2" gutterBottom>Beschreibung</Typography>
                                    <Typography variant="body2">{selectedCourse.description}</Typography>
                                </Grid>
                            </Grid>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={() => setDetailDialogOpen(false)}>Schließen</Button>
                            {selectedCourse.isEnrolled ? (
                                <Button variant="contained" color="success" href={`/#/courses/${selectedCourse.id}/play`}>
                                    Kurs fortsetzen
                                </Button>
                            ) : selectedCourse.license.canEnroll ? (
                                <Button
                                    variant="contained"
                                    onClick={() => {
                                        setDetailDialogOpen(false);
                                        handleEnroll(selectedCourse);
                                    }}
                                >
                                    Jetzt einschreiben
                                </Button>
                            ) : null}
                        </DialogActions>
                    </>
                )}
            </Dialog>

            {/* Settings Dialog */}
            <Dialog open={settingsDialogOpen} onClose={() => setSettingsDialogOpen(false)} maxWidth="sm" fullWidth>
                {settingsCourse && (
                    <>
                        <DialogTitle>Kurseinstellungen - {settingsCourse.title}</DialogTitle>
                        <DialogContent>
                            <Alert severity="info" sx={{ mb: 3 }}>
                                {settingsCourse.license.type === 'reference'
                                    ? 'Dies ist ein Referenz-lizenzierter Kurs. Inhalte werden vom Lizenzgeber verwaltet. Sie können hier die lokalen Einstellungen für Ihr Unternehmen anpassen.'
                                    : 'Dies ist eine Kopie-Lizenz. Der Kurs kann auch über die Kursverwaltung bearbeitet werden.'}
                            </Alert>

                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 1 }}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={settingsCourse.license.isActiveForLicensee}
                                            onChange={(e) => handleUpdateSettings('isActiveForLicensee', e.target.checked)}
                                            disabled={savingSettings}
                                        />
                                    }
                                    label="Kurs aktiv für Nutzer"
                                />
                                <Typography variant="caption" color="text.secondary" sx={{ mt: -2 }}>
                                    Wenn deaktiviert, ist der Kurs für Ihre Nutzer nicht sichtbar.
                                </Typography>

                                <FormControl fullWidth>
                                    <InputLabel>Einschreibemodus</InputLabel>
                                    <Select
                                        value={settingsCourse.license.enrollmentModeForLicensee}
                                        label="Einschreibemodus"
                                        onChange={(e) => handleUpdateSettings('enrollmentModeForLicensee', e.target.value)}
                                        disabled={savingSettings}
                                    >
                                        <MenuItem value="none">Keine Selbsteinschreibung</MenuItem>
                                        <MenuItem value="self">Selbsteinschreibung</MenuItem>
                                        <MenuItem value="request">Einschreibung auf Anfrage</MenuItem>
                                    </Select>
                                </FormControl>
                                <Typography variant="caption" color="text.secondary" sx={{ mt: -2 }}>
                                    Bestimmt, wie sich Nutzer in diesen Kurs einschreiben können.
                                </Typography>
                            </Box>

                            <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                                <Typography variant="subtitle2" gutterBottom>Lizenzinformationen</Typography>
                                <Typography variant="body2">
                                    <strong>Typ:</strong> {settingsCourse.license.type === 'reference' ? 'Referenz' : 'Kopie'}
                                </Typography>
                                <Typography variant="body2">
                                    <strong>Einschreibungen:</strong> {settingsCourse.license.enrollmentStats.current}
                                    {settingsCourse.license.enrollmentStats.max !== null
                                        ? ` / ${settingsCourse.license.enrollmentStats.max}`
                                        : ' (unbegrenzt)'}
                                </Typography>
                                {settingsCourse.license.expiresAt && (
                                    <Typography variant="body2">
                                        <strong>Läuft ab:</strong> {new Date(settingsCourse.license.expiresAt).toLocaleDateString('de-DE')}
                                    </Typography>
                                )}
                            </Box>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={() => setSettingsDialogOpen(false)}>Schließen</Button>
                        </DialogActions>
                    </>
                )}
            </Dialog>

            {/* Enrollments Dialog */}
            <Dialog open={enrollmentsDialogOpen} onClose={() => setEnrollmentsDialogOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle>
                    Einschreibungen - {selectedCourse?.title}
                </DialogTitle>
                <DialogContent>
                    {loadingEnrollments ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                            <CircularProgress />
                        </Box>
                    ) : enrollmentsData ? (
                        <>
                            <Box sx={{ mb: 2 }}>
                                <Typography variant="body2">
                                    Aktuelle Einschreibungen: {enrollmentsData.license.currentEnrollments}
                                    {enrollmentsData.license.maxEnrollments !== null
                                        ? ` / ${enrollmentsData.license.maxEnrollments}`
                                        : ' (unbegrenzt)'}
                                </Typography>
                            </Box>
                            {enrollmentsData.enrollments.length === 0 ? (
                                <Alert severity="info">Noch keine Einschreibungen für diese Lizenz.</Alert>
                            ) : (
                                <TableContainer component={Paper}>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Benutzer</TableCell>
                                                <TableCell>Status</TableCell>
                                                <TableCell>Fortschritt</TableCell>
                                                <TableCell>Eingeschrieben am</TableCell>
                                                <TableCell>Abgeschlossen am</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {enrollmentsData.enrollments.map((enrollment) => (
                                                <TableRow key={enrollment.id}>
                                                    <TableCell>{formatUser(enrollment.userId)}</TableCell>
                                                    <TableCell>
                                                        <Chip
                                                            label={getEnrollmentStatusLabel(enrollment.status)}
                                                            size="small"
                                                            color={enrollment.status === 'completed' ? 'success' : 'default'}
                                                        />
                                                    </TableCell>
                                                    <TableCell>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            <LinearProgress
                                                                variant="determinate"
                                                                value={enrollment.progress}
                                                                sx={{ width: 60 }}
                                                            />
                                                            <Typography variant="caption">{enrollment.progress}%</Typography>
                                                        </Box>
                                                    </TableCell>
                                                    <TableCell>
                                                        {new Date(enrollment.enrolledAt).toLocaleDateString('de-DE')}
                                                    </TableCell>
                                                    <TableCell>
                                                        {enrollment.completedAt
                                                            ? new Date(enrollment.completedAt).toLocaleDateString('de-DE')
                                                            : '-'}
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            )}
                        </>
                    ) : (
                        <Alert severity="warning">Keine Daten verfügbar</Alert>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEnrollmentsDialogOpen(false)}>Schließen</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default LicensedCoursesPage;
